import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import { getBranches, createBranch, updateBranch, deleteBranch, getBranchExpenses, createBranchExpense, deleteBranchExpense } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Building2,
  MapPin,
  Phone,
  Receipt,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminBranchesPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  // Expenses state
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ name: '', amount: '', category: 'renta', frequency: 'mensual', notes: '' });

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    try {
      const res = await getBranches();
      setBranches(res.data);
    } catch (error) {
      toast.error('Error al cargar sucursales');
    }
    setLoading(false);
  };

  const handleOpenModal = (branch = null) => {
    if (branch) {
      setEditingBranch(branch);
      setFormData({ name: branch.name, address: branch.address || '', phone: branch.phone || '' });
    } else {
      setEditingBranch(null);
      setFormData({ name: '', address: '', phone: '' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingBranch) {
        await updateBranch(editingBranch.id, formData);
        toast.success('Sucursal actualizada');
      } else {
        await createBranch(formData);
        toast.success('Sucursal creada');
      }
      setShowModal(false);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setSubmitting(false);
  };

  const handleDelete = async (branch) => {
    if (!window.confirm(`¿Eliminar sucursal "${branch.name}"? Se eliminarán también sus gastos fijos.`)) return;
    try {
      await deleteBranch(branch.id);
      setBranches(branches.filter(b => b.id !== branch.id));
      if (selectedBranch?.id === branch.id) setSelectedBranch(null);
      toast.success('Sucursal eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Expenses
  const openExpenses = async (branch) => {
    setSelectedBranch(branch);
    setLoadingExpenses(true);
    try {
      const res = await getBranchExpenses(branch.id);
      setExpenses(res.data);
    } catch (error) {
      toast.error('Error al cargar gastos');
    }
    setLoadingExpenses(false);
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createBranchExpense(selectedBranch.id, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      toast.success('Gasto agregado');
      setShowExpenseModal(false);
      setExpenseForm({ name: '', amount: '', category: 'renta', frequency: 'mensual', notes: '' });
      const res = await getBranchExpenses(selectedBranch.id);
      setExpenses(res.data);
    } catch (error) {
      toast.error('Error al guardar gasto');
    }
    setSubmitting(false);
  };

  const handleDeleteExpense = async (expense) => {
    if (!window.confirm(`¿Eliminar gasto "${expense.name}"?`)) return;
    try {
      await deleteBranchExpense(selectedBranch.id, expense.id);
      setExpenses(expenses.filter(e => e.id !== expense.id));
      toast.success('Gasto eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Expenses view for selected branch
  if (selectedBranch) {
    const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-fadeIn">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => setSelectedBranch(null)} data-testid="back-to-branches">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-slate-900">{selectedBranch.name}</h1>
              <p className="text-slate-600 mt-1">Gastos fijos de la sucursal</p>
            </div>
            <Button onClick={() => setShowExpenseModal(true)} className="gap-2" data-testid="add-expense-btn">
              <Plus className="w-5 h-5" />
              Nuevo Gasto
            </Button>
          </div>

          {/* Total */}
          <div className="card-base p-6 bg-blue-50 border-blue-200">
            <div className="text-sm text-blue-600 font-medium">Total gastos fijos mensuales</div>
            <div className="text-3xl font-bold text-blue-900">{formatCurrency(totalExpenses)}</div>
          </div>

          {loadingExpenses ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : expenses.length === 0 ? (
            <div className="card-base p-12 text-center text-slate-400">
              <Receipt className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No hay gastos fijos registrados</p>
            </div>
          ) : (
            <div className="card-base overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="table-header">Concepto</th>
                    <th className="table-header">Categoría</th>
                    <th className="table-header">Monto</th>
                    <th className="table-header">Frecuencia</th>
                    <th className="table-header">Notas</th>
                    <th className="table-header text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => (
                    <tr key={expense.id} className="table-row" data-testid={`expense-row-${expense.id}`}>
                      <td className="table-cell font-medium text-slate-900">{expense.name}</td>
                      <td className="table-cell">
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 capitalize">
                          {expense.category || 'otro'}
                        </span>
                      </td>
                      <td className="table-cell font-semibold text-blue-600">{formatCurrency(expense.amount)}</td>
                      <td className="table-cell capitalize text-slate-600">{expense.frequency}</td>
                      <td className="table-cell text-slate-500 text-sm">{expense.notes || '—'}</td>
                      <td className="table-cell text-right">
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteExpense(expense)} data-testid={`delete-expense-${expense.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo Gasto Fijo</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleExpenseSubmit} className="space-y-4">
              <div>
                <Label htmlFor="exp-name">Concepto *</Label>
                <Input id="exp-name" value={expenseForm.name} onChange={(e) => setExpenseForm({ ...expenseForm, name: e.target.value })} placeholder="Ej: Renta local, CFE, SAPAS" required className="mt-1" data-testid="expense-name-input" />
              </div>
              <div>
                <Label>Categoría *</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="expense-category-select">
                    <SelectValue placeholder="Seleccionar categoría..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="renta">Renta</SelectItem>
                    <SelectItem value="luz">Luz</SelectItem>
                    <SelectItem value="agua">Agua</SelectItem>
                    <SelectItem value="internet">Internet</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="exp-amount">Monto (MXN) *</Label>
                  <Input id="exp-amount" type="number" min="0" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} placeholder="5000" required className="mt-1" data-testid="expense-amount-input" />
                </div>
                <div>
                  <Label>Frecuencia</Label>
                  <Select value={expenseForm.frequency} onValueChange={(v) => setExpenseForm({ ...expenseForm, frequency: v })}>
                    <SelectTrigger className="mt-1" data-testid="expense-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensual">Mensual</SelectItem>
                      <SelectItem value="quincenal">Quincenal</SelectItem>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="exp-notes">Notas</Label>
                <Input id="exp-notes" value={expenseForm.notes} onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })} placeholder="Notas adicionales..." className="mt-1" data-testid="expense-notes-input" />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="save-expense-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Guardar Gasto'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // Branches list view
  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Sucursales</h1>
            <p className="text-slate-600 mt-1">Administra las sucursales del negocio</p>
          </div>
          <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-branch-btn">
            <Plus className="w-5 h-5" />
            Nueva Sucursal
          </Button>
        </div>

        {branches.length === 0 ? (
          <div className="card-base p-12 text-center text-slate-400">
            <Building2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No hay sucursales registradas</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {branches.map((branch) => (
              <div key={branch.id} className="card-base p-6" data-testid={`branch-card-${branch.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 text-lg">{branch.name}</h3>
                  </div>
                </div>
                {branch.address && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {branch.address}
                  </div>
                )}
                {branch.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                    <Phone className="w-3.5 h-3.5" />
                    {branch.phone}
                  </div>
                )}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => openExpenses(branch)} data-testid={`expenses-btn-${branch.id}`}>
                    <Receipt className="w-4 h-4" />
                    Gastos Fijos
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenModal(branch)} data-testid={`edit-branch-${branch.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(branch)} data-testid={`delete-branch-${branch.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBranch ? 'Editar Sucursal' : 'Nueva Sucursal'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="branch-name">Nombre *</Label>
              <Input id="branch-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ej: Sucursal Centro" required className="mt-1" data-testid="branch-name-input" />
            </div>
            <div>
              <Label htmlFor="branch-address">Dirección</Label>
              <Input id="branch-address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Calle, Colonia, Ciudad" className="mt-1" data-testid="branch-address-input" />
            </div>
            <div>
              <Label htmlFor="branch-phone">Teléfono</Label>
              <Input id="branch-phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="55 1234 5678" className="mt-1" data-testid="branch-phone-input" />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="save-branch-btn">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Guardar'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
